import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import api from '../api/axios';
import { buildAssetUrl } from '../utils/api';
import { 
  ChevronLeft, MapPin, Maximize2, BedDouble, Bath, Car, 
  Info, Edit3, Check, Image as ImageIcon 
} from 'lucide-react';

import { 
  FaRulerCombined, FaVectorSquare, FaBuilding, FaCompass, 
  FaCalendarAlt, FaIndustry, FaTree, FaSwimmingPool, 
  FaSolarPanel, FaDog, FaWrench 
} from 'react-icons/fa';
import { MdSecurity } from 'react-icons/md';

export default function VerPropiedad() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [propiedad, setPropiedad] = useState(null);
  const [error, setError] = useState('');
  const usuario = JSON.parse(localStorage.getItem('usuario'));

  useEffect(() => {
    api.get(`/properties/${id}`)
      .then(res => {
        const imagenesParseadas = parseImagenes(res.data.imagenes);
        setPropiedad({ ...res.data, imagenes: imagenesParseadas });
      })
      .catch(() => setError('La propiedad no pudo ser encontrada.'));
  }, [id]);

  const parseImagenes = (imagenes) => {
    if (!imagenes) return [];
    const imgsArray = Array.isArray(imagenes) ? imagenes : [imagenes];
    return imgsArray.map(img => {
      try {
        if (typeof img === 'string' && img.trim().startsWith('{')) return JSON.parse(img);
        return typeof img === 'string' ? { url: img } : img;
      } catch (e) { return { url: img }; }
    });
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return buildAssetUrl(cleanPath.includes('uploads') ? cleanPath : `/uploads${cleanPath}`);
  };

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-slate-50">
      <p className="text-slate-500 mb-4 font-medium">{error}</p>
      <button onClick={() => navigate('/')} className="text-sm font-bold uppercase tracking-widest border-b-2 border-slate-900 pb-1">Volver al inicio</button>
    </div>
  );

  if (!propiedad) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-900 animate-spin rounded-full"></div>
    </div>
  );

  const c = propiedad.caracteristicas || {};

  const mostrarDetalle = (Icono, valor, unidad = '', tipo = 'texto') => {
    if (valor == null || valor === false || valor === "") return null;
    return (
      <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100 transition-hover hover:bg-white hover:shadow-sm">
        <div className="text-slate-400 text-lg">{Icono}</div>
        <div className="flex flex-col">
          <span className="text-[10px] uppercase tracking-tighter text-slate-400 font-bold">Dato Técnico</span>
          <span className="text-sm font-semibold text-slate-800">{tipo === 'bool' ? 'Incluido' : `${valor}${unidad}`}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* 1. NAVEGACIÓN SUPERIOR */}
      <nav className="max-w-7xl mx-auto px-6 py-6 flex justify-between items-center">
        <button onClick={() => navigate(-1)} className="group flex items-center gap-2 text-slate-500 hover:text-slate-900">
          <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          <span className="text-xs font-bold uppercase tracking-widest">Regresar</span>
        </button>
        {(usuario?.rol === 'superadmin' || usuario?.id === propiedad.asesor_id) && (
          <Link to={`/editar/${propiedad.id}`} className="flex items-center gap-2 bg-slate-900 text-white px-6 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-slate-700 transition-all">
            <Edit3 size={14} /> Editar
          </Link>
        )}
      </nav>

      {/* 2. TÍTULO Y UBICACIÓN (Separados de la foto para evitar solapamientos) */}
      <header className="max-w-7xl mx-auto px-6 mb-10">
        <div className="flex items-center gap-2 text-blue-600 mb-3">
          <MapPin size={18} />
          <span className="text-sm font-bold uppercase tracking-[0.2em]">{propiedad.ubicacion}</span>
        </div>
        <h1 className="text-4xl md:text-6xl font-serif text-slate-900 leading-tight max-w-4xl">
          {propiedad.titulo}
        </h1>
      </header>

      {/* 3. GALERÍA MURAL */}
      <section className="max-w-7xl mx-auto px-6 mb-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 h-auto md:h-[600px]">
          <div className="md:col-span-2 h-[400px] md:h-full">
            <img
              src={getImageUrl(propiedad.foto_principal)}
              alt="Principal"
              className="w-full h-full object-cover rounded-3xl shadow-lg"
              onError={(e) => { e.target.src = 'https://via.placeholder.com/1200x800?text=VEstates+Property'; }}
            />
          </div>
          <div className="hidden md:grid col-span-2 grid-cols-2 grid-rows-2 gap-4 h-full">
            {[0, 1, 2, 3].map((idx) => {
              const imgData = propiedad.imagenes?.[idx];
              const url = imgData ? getImageUrl(imgData.url || imgData) : null;
              return (
                <div key={idx} className="relative rounded-2xl overflow-hidden bg-slate-100">
                  {url ? (
                    <img src={url} alt="Extra" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300"><ImageIcon size={24} /></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. CONTENIDO PRINCIPAL Y SIDEBAR */}
      <main className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-16">
        <div className="lg:col-span-2 space-y-16">
          {/* Quick Info Bar */}
          <div className="flex flex-wrap gap-8 py-8 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <Maximize2 className="text-slate-400" />
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">Superficie</p>
                <p className="text-sm font-semibold">{c.area} m²</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <BedDouble className="text-slate-400" />
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">Recámaras</p>
                <p className="text-sm font-semibold">{c.recamaras}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Bath className="text-slate-400" />
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">Baños</p>
                <p className="text-sm font-semibold">{c.banos}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Car className="text-slate-400" />
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">Cochera</p>
                <p className="text-sm font-semibold">{c.estacionamiento}</p>
              </div>
            </div>
          </div>

          {/* Descripción */}
          <section>
            <h2 className="text-xs font-black uppercase tracking-[0.3em] text-slate-400 mb-6 flex items-center gap-2">
              <Info size={14} /> Memoria Descriptiva
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed font-light whitespace-pre-line">
              {propiedad.descripcion}
            </p>
          </section>

          {/* Ficha Técnica */}
          <section>
            <h2 className="text-xs font-black uppercase tracking-[0.3em] text-slate-400 mb-8">Especificaciones</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {mostrarDetalle(<FaRulerCombined />, c.area, ' m²')}
              {mostrarDetalle(<FaVectorSquare />, c.terreno, ' m²')}
              {mostrarDetalle(<FaBuilding />, c.niveles, ' Niveles')}
              {mostrarDetalle(<FaCompass />, c.orientacion)}
              {mostrarDetalle(<FaCalendarAlt />, c.antiguedad)}
              {mostrarDetalle(<FaIndustry />, c.tipo_construccion)}
              {mostrarDetalle(<FaTree />, c.jardin, '', 'bool')}
              {mostrarDetalle(<FaSwimmingPool />, c.alberca, '', 'bool')}
              {mostrarDetalle(<FaSolarPanel />, c.paneles_solares, '', 'bool')}
              {mostrarDetalle(<MdSecurity />, c.vigilancia, '', 'bool')}
              {mostrarDetalle(<FaDog />, c.pet_friendly, '', 'bool')}
              {mostrarDetalle(<FaWrench />, c.estado_conservacion)}
            </div>
          </section>
        </div>

        {/* Sidebar Sticky */}
        <aside className="lg:col-span-1">
          <div className="sticky top-12 p-8 border border-slate-100 rounded-3xl shadow-xl bg-white space-y-8">
            <div>
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600 block mb-2">Inversión Estimada</span>
              <p className="text-4xl font-semibold text-slate-900">
                ${Number(propiedad.precio).toLocaleString('es-MX')} 
                <span className="text-sm text-slate-400 ml-2 font-normal italic">MXN</span>
              </p>
            </div>
            <div className="space-y-3 pt-6 border-t border-slate-100">
              <button className="w-full bg-slate-900 text-white py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-blue-700 transition-all">
                Solicitar Información
              </button>
              <button className="w-full bg-white border border-slate-200 text-slate-900 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-slate-50 transition-colors">
                Agendar Visita
              </button>
            </div>
            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
              <Check className="text-green-500" size={20} strokeWidth={3} />
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold leading-tight">
                Listado verificado<br/>Oficialmente
              </span>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}
